import React, { useState } from "react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HelpCircle, X, Send, Loader2 } from "lucide-react";

export default function SupportBubble() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    subject: "Something else",
    message: "",
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) return toast.error("Give me a bit more detail.");
    setSending(true);
    try {
      const res = await fetch(`${API}/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: user?.name || "Anonymous",
          email: user?.email || "anonymous@viral-co-hq.com",
          subject: form.subject,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
        setForm({ subject: "Something else", message: "" });
      }, 2500);
    } catch (err) {
      toast.error("Could not send. Try /support instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="support-bubble"
          onClick={() => setOpen(true)}
          aria-label="Support"
          className="fixed bottom-6 right-6 z-40 w-12 h-12 bg-signal-yellow text-black flex items-center justify-center hover:bg-signal-yellow-hover transition-colors shadow-lg"
        >
          <HelpCircle size={20} />
        </button>
      )}
      {open && (
        <div
          data-testid="support-popover"
          className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] sm:w-96 brutal-border-strong bg-[#0A0A0A] shadow-2xl"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HelpCircle size={14} className="text-signal-yellow" />
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-400">// help desk</span>
            </div>
            <button
              data-testid="support-close"
              onClick={() => setOpen(false)}
              className="text-zinc-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          {sent ? (
            <div className="p-6 text-center space-y-3">
              <div className="text-signal-yellow font-mono text-2xl">✓</div>
              <p className="font-mono text-xs text-zinc-300">Ticket received. Reply within 24h.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="p-4 space-y-3">
              <label className="block space-y-1">
                <span className="label-eyebrow block">Type</span>
                <select
                  data-testid="bubble-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-[#141414] border border-white/10 text-white p-2 font-mono text-xs focus:border-signal-yellow focus:outline-none"
                >
                  <option>Bug or error</option>
                  <option>Billing / payment</option>
                  <option>Feature request</option>
                  <option>Account access</option>
                  <option>Refund request</option>
                  <option>Something else</option>
                </select>
              </label>
              <label className="block space-y-1">
                <span className="label-eyebrow block">Message</span>
                <textarea
                  data-testid="bubble-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="what's up?"
                  required
                  minLength={5}
                  className="w-full bg-[#141414] border border-white/10 text-white placeholder:text-zinc-600 p-2 font-mono text-xs min-h-[100px] focus:border-signal-yellow focus:outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={sending}
                data-testid="bubble-send"
                className="w-full bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-[11px] px-4 py-2 hover:bg-signal-yellow-hover transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {sending ? "Sending..." : "Send"}
              </button>
              <div className="text-center">
                <a href="/support" className="font-mono text-[10px] text-zinc-500 hover:text-signal-yellow tracking-[0.18em] uppercase">
                  Full form →
                </a>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}
